import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Clock, CheckCircle2, XCircle } from "lucide-react";
import { api, money, fmtDate, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { TIER_META } from "../lib/tiers";
import { PackageForm } from "../components/broker/PackageForm";

const BADGE = {
  pending_review: "bg-amber-400/15 text-amber-300", approved: "bg-emerald-500/15 text-emerald-300",
  rejected: "bg-rose-500/15 text-rose-300",
};
const STATUS_TEXT = {
  pending: { icon: Clock, cls: "text-amber-300", text: "Your partner application is under review. You can submit packages once approved." },
  approved: { icon: CheckCircle2, cls: "text-emerald-300", text: "You're an approved partner. Submit packages — each one is reviewed before going live." },
  rejected: { icon: XCircle, cls: "text-rose-300", text: "Your application wasn't approved. Contact us if you think this is a mistake." },
};

export default function BrokerDashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [form, setForm] = useState({ open: false, item: null });
  const load = useCallback(() => api.get("/brokers/me").then((r) => setD(r.data)).catch(() => setD(false)), []);
  useEffect(() => { if (user?.role === "broker") load(); }, [user, load]);
  if (user && user.role !== "broker") return <Navigate to="/dashboard" replace />;
  if (d === false) return <div data-testid="broker-missing" className="min-h-[60vh] grid place-items-center px-6 text-center text-slate-400 text-sm">We couldn't find your partner profile. Please contact the VoyageQueue team.</div>;
  if (!d) return <div className="min-h-[60vh] grid place-items-center text-slate-500 font-mono-lux text-sm">Loading…</div>;

  const st = STATUS_TEXT[d.broker.status];
  const Icon = st.icon;
  const totalFeePct = d.fees.booking_fee_pct + d.fees.commission_pct + d.fees.owner_pct;
  const save = async (body) => {
    try {
      if (form.item) await api.put(`/brokers/packages/${form.item.id}`, body);
      else await api.post("/brokers/packages", body);
      toast.success("Package submitted for review");
      setForm({ open: false, item: null });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this package?")) return;
    try { await api.delete(`/brokers/packages/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const payout = d.bookings.reduce((a, b) => a + b.broker_payout, 0);

  return (
    <div data-testid="broker-dashboard" className="max-w-7xl mx-auto px-5 md:px-10 py-14">
      <p className="eyebrow">Partner portal</p>
      <h1 className="font-serif-lux text-4xl sm:text-5xl mt-4">{d.broker.company}</h1>
      <div data-testid="broker-status" className="lux-card p-5 mt-6 flex items-center gap-3">
        <Icon className={`w-5 h-5 ${st.cls}`} /><p className="text-sm text-slate-300">{st.text}</p>
      </div>
      <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-6">
        <div className="lux-card p-3 sm:p-5 min-w-0"><p className="eyebrow !text-[9px] sm:!text-xs">Packages</p><p data-testid="broker-package-count" className="font-mono-lux text-lg sm:text-2xl mt-3 truncate">{d.packages.length}</p></div>
        <div className="lux-card p-3 sm:p-5 min-w-0"><p className="eyebrow !text-[9px] sm:!text-xs">Bookings</p><p className="font-mono-lux text-lg sm:text-2xl mt-3 truncate">{d.bookings.length}</p></div>
        <div className="lux-card p-3 sm:p-5 min-w-0"><p className="eyebrow !text-[9px] sm:!text-xs">Payouts</p><p data-testid="broker-payout-total" className="font-mono-lux text-lg sm:text-2xl mt-3 truncate text-[#38BDF8]">{money(payout)}</p></div>
      </div>
      <p className="text-xs text-slate-500 mt-4">Fees: first booked package free, then {totalFeePct}% of trip cost ({d.fees.booking_fee_pct}% booking fee + {d.fees.commission_pct}% commission + {d.fees.owner_pct}% service share). You receive {100 - totalFeePct}%.</p>

      <div className="flex items-center justify-between mt-12">
        <h2 className="font-serif-lux text-2xl">Your packages</h2>
        <button data-testid="broker-add-package-btn" disabled={d.broker.status !== "approved"} onClick={() => setForm({ open: true, item: null })} className="btn-gold px-5 py-2.5 text-sm inline-flex items-center gap-2"><Plus className="w-4 h-4" />Submit package</button>
      </div>
      <div className="mt-5 space-y-3">
        {d.packages.map((p) => (
          <div key={p.id} data-testid={`broker-package-${p.id}`} className="lux-card p-5 flex flex-wrap items-center gap-4 justify-between">
            <div className="min-w-0">
              <p className="font-semibold">{p.title}</p>
              <p className="text-xs text-slate-400 mt-1">{TIER_META[p.tier]?.short} · {p.location} · {p.nights} nights · {money(p.broker_price)}</p>
              {p.reject_reason && <p className="text-xs text-rose-300 mt-1">Reason: {p.reject_reason}</p>}
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] font-mono-lux uppercase px-3 py-1 rounded-full ${BADGE[p.review_status]}`}>{p.review_status.replace("_", " ")}</span>
              {p.times_awarded === 0 && <button onClick={() => setForm({ open: true, item: p })} className="btn-ghost px-3 py-1.5 text-xs">Edit</button>}
              {p.times_awarded === 0 && <button onClick={() => remove(p.id)} className="btn-ghost px-3 py-1.5 text-xs">Remove</button>}
            </div>
          </div>
        ))}
        {d.packages.length === 0 && <p className="text-sm text-slate-500">No packages yet.</p>}
      </div>

      <h2 className="font-serif-lux text-2xl mt-12">Bookings & payouts</h2>
      <div className="lux-card mt-5 overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="text-left text-xs text-slate-500"><tr className="border-b border-white/5">
            <th className="p-4">Trip</th><th>Date</th><th>Trip cost</th><th>Fees</th><th>Your payout</th><th>Status</th></tr></thead>
          <tbody>
            {d.bookings.map((b) => (
              <tr key={b.id} className="border-b border-white/5">
                <td className="p-4">{b.vacation_title}</td><td>{fmtDate(b.created_at)}</td>
                <td className="font-mono-lux">{money(b.trip_cost)}</td>
                <td className="font-mono-lux text-xs">{b.free_first_booking ? "Free first booking" : money(b.total_fee)}</td>
                <td className="font-mono-lux text-[#38BDF8]">{money(b.broker_payout)}</td>
                <td className="text-xs uppercase font-mono-lux">{b.payout_status}</td>
              </tr>
            ))}
            {d.bookings.length === 0 && <tr><td colSpan={6} className="p-6 text-slate-500">No bookings yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <PackageForm open={form.open} onOpenChange={(o) => setForm({ ...form, open: o })} initial={form.item} budgets={d.budgets} onSave={save} />
    </div>
  );
}
